import React, { type ReactElement, type ReactNode, useContext } from 'react';

import type { MessageDescriptor } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Text, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { TeamInlinePreviewContext } from '../../store/context';

type DescriptionText = string | ReactElement<MessageDescriptor> | ReactNode;

const styles = cssMap({
	description: {
		color: token('color.text.subtlest'),
		font: token('font.body.small'),
		flexWrap: 'nowrap',
	},
});

/**
 * Internal component that renders the team name and optional description text.
 * Handles truncation detection and conditionally displays verified/archived badges.
 */
function Description({ description }: { description?: DescriptionText }): React.JSX.Element | null {
	const { singleLine, getTestId } = useContext(TeamInlinePreviewContext);
	const maxLines = singleLine ? 1 : undefined;

	if (!description) {
		return null;
	}

	if (typeof description === 'string') {
		return (
			<Text
				size="small"
				maxLines={maxLines}
				color="color.text.subtle"
				align="start"
				testId={getTestId('team-description')}
			>
				{description}
			</Text>
		);
	}

	return (
		<Flex testId={getTestId('team-description')} xcss={styles.description}>
			{description}
		</Flex>
	);
}

export { Description };
